import { appPaths } from "@clockwise/web-commons/src/constants/route.constants";
import AvatarMenu from "@clockwise/web-commons/src/ui/avatar-menu";
import { NavContext } from "@clockwise/web-commons/src/ui/home-nav/home-nav";
import TwoColumnLayout from "@clockwise/web-commons/src/ui/layouts/TwoColumnLayout";
import { homeNavCollapsed } from "@clockwise/web-commons/src/util/local-storage";
import { useRouter } from "found";
import { SignOut } from "phosphor-react";
import React, { ReactNode, useEffect, useMemo } from "react";
import { useOnboarding } from "../../hooks/useOnboarding/useOnboarding";
import { TrackingEvents, track } from "../../util/analytics.util";
import { logger } from "../../util/logger.util";
import { tailwindScreenSize } from "../../util/tailwindValues";
import { useLogout } from "../hooks/useLogout";
import { useUserProfile } from "../hooks/useUserProfile";

type Props = {
  secondary?: ReactNode;
  data?: {
    withToolbar?: boolean;
    collapsedSidebar?: boolean;
  };
  banner?: ReactNode;
  collapsedSidebar?: boolean;
};

export const determineCollapseDefault = (userId?: string | null) => {
  if (homeNavCollapsed.isNull()) {
    const screenSize = window.innerWidth;
    const smallScreen = tailwindScreenSize("sm");

    if (!smallScreen) {
      userId &&
        track(TrackingEvents.WEB_LAYOUT.COLLAPSE_EXPAND.NO_DEFINITION_OF_SMALL_SCREEN, {
          userId: userId,
        });
      return false;
    }

    if (screenSize <= smallScreen) {
      userId &&
        track(TrackingEvents.WEB_LAYOUT.COLLAPSE_EXPAND.SMALL_SCREEN_DEFAULT, {
          userId: userId,
        });
      return true;
    }

    userId &&
      track(TrackingEvents.WEB_LAYOUT.COLLAPSE_EXPAND.BIG_SCREEN_DEFAULT, {
        userId: userId,
      });
    return false;
  }

  return homeNavCollapsed.get();
};

const ClockwiseWebLayout: React.FC<Props> = ({ data, children, secondary, banner }) => {
  const { router } = useRouter();
  const { userProfile } = useUserProfile();
  const { userId } = userProfile;

  const { shouldRenderOnboarding } = useOnboarding();
  const [collapsed, setCollapsed] = React.useState(determineCollapseDefault(userId));
  const [scrollbared, setScrollbared] = React.useState(false);

  const handleToggleSidebar = () => {
    setCollapsed((collapsedState) => {
      const newCollapsedState = !collapsedState;

      const collapseOrExpandTrackingEvent = newCollapsedState
        ? TrackingEvents.WEB_LAYOUT.COLLAPSE_EXPAND.USER_COLLAPSED
        : TrackingEvents.WEB_LAYOUT.COLLAPSE_EXPAND.USER_EXPANDED;

      userId &&
        track(collapseOrExpandTrackingEvent, {
          userId: userId,
        });
      homeNavCollapsed.set(newCollapsedState);

      return newCollapsedState;
    });
  };

  const shouldCollapseSidebar = useMemo(() => !!data?.collapsedSidebar, [data]);

  useEffect(() => {
    if (shouldCollapseSidebar) {
      // if AI, set navbar collapsed to true, but do not override local storage
      setCollapsed(true);
    } else {
      // if any other page, use localstorage settings
      setCollapsed(determineCollapseDefault(userId));
    }
  }, [shouldCollapseSidebar]);

  const [logoutUser] = useLogout({
    onError: (error) => logger.error("An error occurred on logout", error),
  });

  const handleLogout = () => {
    void logoutUser();
  };

  if (shouldRenderOnboarding) {
    router.replace(appPaths.onboarding);
    return null;
  }

  return (
    <NavContext.Provider
      value={{
        collapsed,
        isFreePlan: false,
        scrollbared,
        setScrollbared,
        toggleCollapsed: handleToggleSidebar,
      }}
    >
      {banner}
      <TwoColumnLayout
        secondary={secondary}
        toolbar={
          data?.withToolbar && (
            <AvatarMenu
              user={userProfile}
              menuItems={[
                {
                  Icon: SignOut,
                  text: "sign out",
                  action: handleLogout,
                },
              ]}
            />
          )
        }
      >
        {children}
      </TwoColumnLayout>
    </NavContext.Provider>
  );
};

export default ClockwiseWebLayout;
